import { isEqual } from 'lodash-es';
import React, { FC, useCallback, useEffect, useRef } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Dropdown, Icon, Input, OptionGroup, OptionProps, Tooltip } from '@column/column-ui-kit';

import { EntryClassCodeDefinitions } from '../TypeContent/ach';
import { ROUTE } from '~/app/routes';
import { Loading, PageHeader, RenderFields } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { useBankAccountDropdown } from '~/hooks';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import {
  useCreateTransferTemplate,
  useGetTransferTemplate,
  useUpdateTransferTemplate,
} from '~/hooks/useTransferTemplates';
import { useNavigate } from '~/lib/navigation';
import { TransferTemplateResponse, TransferTemplateType } from '~/repositories/Transfer/TransferTemplateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { log, removeEmptyString } from '~/util';

import { EditTemplateTable } from './_partial/EditTemplateTable';
import { isACHTransferAuthorized } from './_utils/ach';
import {
  buildCreateTransferTemplateRequest,
  buildUpdateTransferTemplateRequest,
  validateTransfers,
} from './_utils/api';
import { isBookTransferAuthorized } from './_utils/book';
import { isWireTransferAuthorized } from './_utils/wire';
import { TransferTemplateForm } from './types';

interface Params {
  id: string;
}

interface LocationState {
  defaultFormValues?: TransferTemplateForm | null;
}

const Wrapper = styled.div`
  padding: 0 0 24px 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 24px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    .hide-mobile {
      display: none;
    }
  }
`;

const StyledOption = styled(OptionGroup.Option)<OptionProps>`
  opacity: 1;

  ${OptionGroup.Option.Label} {
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  }
`;

const OptionCheck = styled(Icon.CircleCheck)`
  --icon-size: 20px;
  --icon-color: ${({ theme }) => theme.primary.background};
  margin: -2px 0 -2px -2px;
`;

export const PageTransferTemplatesEdit: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const { openAlert } = useDialog();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { isAuthorized } = useSessionStore();
  const navigate = useNavigate();
  const { handleFormErrors } = useFormErrorHandler();

  const location = useLocation();
  const locationState = location.state as LocationState;

  const initialValues = useRef<Partial<TransferTemplateResponse> | null>(null);

  const { createRequest: getTransferTemplate, isLoading: isLoadingGetTransferTemplate } = useGetTransferTemplate({
    onError: (error) => {
      addDangerNotification({
        content: error.message,
      });
      navigate(`${ROUTE.TRANSFERS}/templates`);
    },
  });

  const { createRequest: createTransferTemplate } = useCreateTransferTemplate({
    onError: (error) => {
      addDangerNotification({
        content: error.message,
      });
    },
    onSuccess: () => {
      addSuccessNotification({
        content: 'Transfer template created successfully',
      });
    },
  });

  const { createRequest: updateTransferTemplate } = useUpdateTransferTemplate({
    onError: (error) => {
      addDangerNotification({
        content: error.message,
      });
    },
    onSuccess: () => {
      addSuccessNotification({
        content: 'Transfer template updated successfully',
      });
    },
  });

  const defaultFormValues: Partial<TransferTemplateForm> = locationState?.defaultFormValues ?? {
    templateType: TransferTemplateType.ACH,
    isPartialAccess: false,
    achDefaultParameters: {},
    achTransfers: [],
    wireDefaultParameters: {},
    wireTransfers: [],
    bookDefaultParameters: {},
    visibleColumns: ['amount', 'counterpartyId', 'type', 'bankAccountId', 'description', 'entryClassCode'],
  };

  const methods = useForm<TransferTemplateForm>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });

  const { handleSubmit, reset, watch, setValue, getValues } = methods;
  const achTransfers = watch('achTransfers');
  const wireTransfers = watch('wireTransfers');
  const bookTransfers = watch('bookTransfers');
  const isTemplateSendable =
    (achTransfers ?? []).every((transfer) => isACHTransferAuthorized(transfer, isAuthorized)) &&
    (wireTransfers ?? []).every((transfer) => isWireTransferAuthorized(transfer, isAuthorized)) &&
    (bookTransfers ?? []).every((transfer) => isBookTransferAuthorized(transfer, isAuthorized));

  const {
    options: achDefaultParametersBankAccountOptions,
    hiddenOptions: achDefaultParametersBankAccountHiddenOptions,
    searchBankAccounts: searchAchDefaultParametersBankAccount,
  } = useBankAccountDropdown(watch('achDefaultParameters.bankAccountId'));

  const {
    options: wireDefaultParametersBankAccountOptions,
    hiddenOptions: wireDefaultParametersBankAccountHiddenOptions,
    searchBankAccounts: searchWireDefaultParametersBankAccount,
  } = useBankAccountDropdown(watch('wireDefaultParameters.bankAccountId'));

  const {
    options: senderBookDefaultParametersBankAccountOptions,
    hiddenOptions: senderBookDefaultParametersBankAccountHiddenOptions,
    searchBankAccounts: searchSenderBookDefaultParametersBankAccount,
  } = useBankAccountDropdown(watch('bookDefaultParameters.senderBankAccountId'));

  const {
    options: receiverBookDefaultParametersBankAccountOptions,
    hiddenOptions: receiverBookDefaultParametersBankAccountHiddenOptions,
    searchBankAccounts: searchReceiverBookDefaultParametersBankAccount,
  } = useBankAccountDropdown(watch('bookDefaultParameters.receiverBankAccountId'));

  useEffect(() => {
    if (id && !locationState?.defaultFormValues) {
      getTransferTemplate({ transferTemplateId: id }).then((data) => {
        if (!data) {
          return;
        }

        const newTransferTemplate = { ...data } as Partial<TransferTemplateResponse>;
        delete newTransferTemplate?.version;

        reset(newTransferTemplate);
        initialValues.current = newTransferTemplate;
      });
    }

    if (locationState?.defaultFormValues) {
      reset(locationState.defaultFormValues);
      initialValues.current = locationState.defaultFormValues;
    }
  }, [id, locationState?.defaultFormValues]);

  const onSuccess = useCallback(
    async (transferTemplate: TransferTemplateForm) => {
      // Validate the transfer template
      const validation = validateTransfers(transferTemplate.templateType, transferTemplate);
      if (!validation.isValid) {
        validation.errors.forEach((error) => {
          addDangerNotification({ content: error.message });
        });
        return;
      }

      if (id) {
        // Update existing template
        const updateRequest = buildUpdateTransferTemplateRequest(transferTemplate, id);
        const newData = await updateTransferTemplate(updateRequest);

        if (newData) {
          reset(newData);
        }

        return;
      }

      try {
        // Create new template
        const createRequest = buildCreateTransferTemplateRequest(transferTemplate);
        const newData = await createTransferTemplate(createRequest);

        navigate(`${ROUTE.TRANSFERS}/templates/edit/${newData?.transferTemplateId}`, { replace: true });
      } catch (error) {
        addDangerNotification({
          content: error instanceof Error ? error.message : 'An unknown error occurred',
        });
      }
    },
    [id, addDangerNotification, updateTransferTemplate, reset, isAuthorized, createTransferTemplate, navigate]
  );

  const onError: SubmitHandler<FieldValues> = useCallback((errors) => {
    handleFormErrors({ errors });
  }, []);

  const handleCancelClick = useCallback(() => {
    reset();
    navigate(`${ROUTE.TRANSFERS}/templates`, { state: { defaultFormValues: null } });
  }, []);

  const handleReviewClick = () => {
    const normalizeTransfers = (obj: Record<string, unknown>) => {
      const normalized = JSON.parse(JSON.stringify(obj));

      const processTransfers = (
        key: string,
        transformFn: (transfer: Record<string, unknown>) => Record<string, unknown>
      ) => {
        if (normalized[key]) {
          normalized[key] = normalized[key].map(transformFn);
        }
      };

      processTransfers('wireTransfers', (transfer) => ({
        ...transfer,
        amount: String(transfer.amount),
        bankAccountDescription: undefined,
        counterpartyDescription: undefined,
      }));

      processTransfers('achTransfers', (transfer) => ({
        ...transfer,
        amount: String(transfer.amount),
        bankAccountDescription: undefined,
        counterpartyDescription: undefined,
      }));

      processTransfers('bookTransfers', (transfer) => ({
        ...transfer,
        amount: String(transfer.amount),
        senderBankAccountDescription: undefined,
        receiverBankAccountDescription: undefined,
      }));

      return normalized;
    };

    const initial = removeEmptyString(initialValues.current ?? {});
    const current = removeEmptyString(watch());

    const cleanInitial = normalizeTransfers(initial as Record<string, unknown>);
    const cleanCurrent = normalizeTransfers(current as Record<string, unknown>);

    const hasChanges = !isEqual(cleanInitial, cleanCurrent);

    log({ type: 'info', name: 'ReviewTransferTemplate', context: { hasChanges, cleanInitial, cleanCurrent } });

    if (!hasChanges) {
      navigate(`${ROUTE.TRANSFERS}/templates/review/${id}`, {
        state: { defaultFormValues: watch() },
      });
      return;
    }

    openAlert({
      headline: 'Review Transfer Template',
      content: 'Review the transfer template and make any necessary changes.',
      confirmButton: {
        label: 'Continue',
        variant: 'primary',
        fullWidth: true,
      },
      cancelButton: {
        label: 'Save & Continue',
        variant: 'secondary',
        fullWidth: true,
      },
      icon: 'CircleQuestionmark',
      type: 'primary',
      onSuccess: () => {
        navigate(`${ROUTE.TRANSFERS}/templates/review/${id}`, {
          state: { defaultFormValues: watch() },
        });
      },
      onClose: () => {
        onSuccess(getValues()).then(() => {
          navigate(`${ROUTE.TRANSFERS}/templates/review/${id}`, { state: { defaultFormValues: null } });
        });
      },
    });
  };

  const isLoading = isLoadingGetTransferTemplate && !!id && !locationState?.defaultFormValues;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSuccess, onError)}>
        <PageHeader text={id ? 'Edit Transfer Template' : 'Create Transfer Template'}>
          <Button type="button" variant="secondary" size="small" onClick={handleCancelClick}>
            Cancel
          </Button>
          <Button type="submit" size="small">
            {id ? 'Save' : 'Create'}
            <span className="hide-mobile"> Template</span>
          </Button>
          {id && (
            <Tooltip
              content="You do not have permission to send this template."
              isOpen={!isTemplateSendable ? undefined : false}
              placement="left"
            >
              <Button type="button" size="small" onClick={handleReviewClick} isDisabled={!isTemplateSendable}>
                Review
              </Button>
            </Tooltip>
          )}
        </PageHeader>

        <Loading isLoading={isLoading}>
          <Wrapper>
            {RenderFields<TransferTemplateResponse>({
              sections: [
                {
                  fields: [
                    {
                      id: 'description',
                      label: 'Template Name',
                      defaultValue: '',
                      rules: {
                        required: true,
                      },
                      children: ({ value, onChange, onBlur }, { isTouched, error }) => (
                        <Input
                          placeholder="Template Name"
                          hasError={isTouched && !!error}
                          value={String(value)}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      ),
                    },
                    {
                      id: 'templateType',
                      label: 'Template Type',
                      defaultValue: TransferTemplateType.ACH,
                      children: ({ value, onChange }) => (
                        <OptionGroup orientation={OptionGroup.Orientation.Horizontal}>
                          {((value === TransferTemplateType.ACH && id) || !id) && (
                            <StyledOption
                              isActive={value === TransferTemplateType.ACH}
                              onClick={() => !id && onChange(TransferTemplateType.ACH)}
                              isDisabled={!!id}
                            >
                              {id ? <OptionCheck /> : <OptionGroup.Option.Radio />}
                              <OptionGroup.Option.Label>ACH</OptionGroup.Option.Label>
                            </StyledOption>
                          )}

                          {((value === TransferTemplateType.Wire && id) || !id) && (
                            <StyledOption
                              isActive={value === TransferTemplateType.Wire}
                              onClick={() => !id && onChange(TransferTemplateType.Wire)}
                              isDisabled={!!id}
                            >
                              {id ? <OptionCheck /> : <OptionGroup.Option.Radio />}
                              <OptionGroup.Option.Label>Wire</OptionGroup.Option.Label>
                            </StyledOption>
                          )}

                          {((value === TransferTemplateType.Book && id) || !id) && (
                            <StyledOption
                              isActive={value === TransferTemplateType.Book}
                              onClick={() => !id && onChange(TransferTemplateType.Book)}
                              isDisabled={!!id}
                            >
                              {id ? <OptionCheck /> : <OptionGroup.Option.Radio />}
                              <OptionGroup.Option.Label>Book</OptionGroup.Option.Label>
                            </StyledOption>
                          )}
                        </OptionGroup>
                      ),
                    },
                  ],
                },
                {
                  headline: (
                    <>
                      Default Parameters <small>(Optional)</small>
                    </>
                  ),
                  description: 'These parameters apply to any new transfers added to the transfers list below',
                  fields: [
                    {
                      hide: watch('templateType') !== TransferTemplateType.ACH,
                      id: 'achDefaultParameters.bankAccountId',
                      defaultValue: '',
                      label: 'Bank Account',
                      description: 'Select a bank account to originate the ACH transfer.',
                      children: ({ value, onBlur }, { error }) => (
                        <Dropdown
                          options={achDefaultParametersBankAccountOptions ?? []}
                          hiddenOptions={achDefaultParametersBankAccountHiddenOptions ?? []}
                          active={value}
                          fullWidth
                          maxWidth="640px"
                          search
                          searchLabel="Search for description"
                          onSearchChange={(description: string) =>
                            searchAchDefaultParametersBankAccount({ description })
                          }
                          onChange={(baccId) => setValue('achDefaultParameters.bankAccountId', baccId)}
                          onOpenChange={(open) => !open && onBlur()}
                          hasError={!!error}
                        />
                      ),
                    },
                    {
                      hide: watch('templateType') !== TransferTemplateType.Wire,
                      id: 'wireDefaultParameters.bankAccountId',
                      defaultValue: '',
                      label: 'Bank Account',
                      description: 'Select a bank account to originate the Wire transfer.',
                      children: ({ value, onBlur }, { error }) => (
                        <Dropdown
                          options={wireDefaultParametersBankAccountOptions ?? []}
                          hiddenOptions={wireDefaultParametersBankAccountHiddenOptions ?? []}
                          active={value}
                          fullWidth
                          maxWidth="640px"
                          search
                          searchLabel="Search for description"
                          onSearchChange={(description: string) =>
                            searchWireDefaultParametersBankAccount({ description })
                          }
                          onChange={(baccId) => setValue('wireDefaultParameters.bankAccountId', baccId)}
                          onOpenChange={(open) => !open && onBlur()}
                          hasError={!!error}
                        />
                      ),
                    },
                    {
                      hide: watch('templateType') !== TransferTemplateType.Book,
                      id: 'bookDefaultParameters.senderBankAccountId',
                      defaultValue: '',
                      label: 'Sender Bank Account',
                      description: 'Select a bank account to send the book transfer from.',
                      children: ({ value, onBlur }, { error }) => (
                        <Dropdown
                          options={senderBookDefaultParametersBankAccountOptions ?? []}
                          hiddenOptions={senderBookDefaultParametersBankAccountHiddenOptions ?? []}
                          active={value}
                          fullWidth
                          maxWidth="640px"
                          search
                          searchLabel="Search for description"
                          onSearchChange={(description: string) =>
                            searchSenderBookDefaultParametersBankAccount({ description })
                          }
                          onChange={(baccId) => setValue('bookDefaultParameters.senderBankAccountId', baccId)}
                          onOpenChange={(open) => !open && onBlur()}
                          hasError={!!error}
                        />
                      ),
                    },
                    {
                      hide: watch('templateType') !== TransferTemplateType.Book,
                      id: 'bookDefaultParameters.receiverBankAccountId',
                      defaultValue: '',
                      label: 'Receiver Bank Account',
                      description: 'Select a bank account to send the book transfer to.',
                      children: ({ value, onBlur }, { error }) => (
                        <Dropdown
                          options={receiverBookDefaultParametersBankAccountOptions ?? []}
                          hiddenOptions={receiverBookDefaultParametersBankAccountHiddenOptions ?? []}
                          active={value}
                          fullWidth
                          maxWidth="640px"
                          search
                          searchLabel="Search for description"
                          onSearchChange={(description: string) =>
                            searchReceiverBookDefaultParametersBankAccount({ description })
                          }
                          onChange={(baccId) => setValue('bookDefaultParameters.receiverBankAccountId', baccId)}
                          onOpenChange={(open) => !open && onBlur()}
                          hasError={!!error}
                        />
                      ),
                    },
                    {
                      id: 'achDefaultParameters.type',
                      hide: watch('templateType') !== TransferTemplateType.ACH,
                      label: 'Type',
                      description: 'Select between ACH Debit or ACH Credit.',
                      defaultValue: 'DEBIT',
                      children: ({ value, onChange }) => (
                        <OptionGroup orientation={OptionGroup.Orientation.Horizontal}>
                          <OptionGroup.Option isActive={value === 'DEBIT'} onClick={() => onChange('DEBIT')}>
                            <OptionGroup.Option.Radio />
                            <OptionGroup.Option.Label>Debit</OptionGroup.Option.Label>
                            <OptionGroup.Option.Description>
                              Pull money from a counterparty to your bank account.
                            </OptionGroup.Option.Description>
                          </OptionGroup.Option>

                          <OptionGroup.Option isActive={value === 'CREDIT'} onClick={() => onChange('CREDIT')}>
                            <OptionGroup.Option.Radio />
                            <OptionGroup.Option.Label>Credit</OptionGroup.Option.Label>
                            <OptionGroup.Option.Description>
                              Push money from your bank account to a counterparty.
                            </OptionGroup.Option.Description>
                          </OptionGroup.Option>
                        </OptionGroup>
                      ),
                    },
                    {
                      id: 'achDefaultParameters.entryClassCode',
                      hide: watch('templateType') !== TransferTemplateType.ACH,
                      label: 'Entry Class Code',
                      defaultValue: 'CCD',
                      tooltip: {
                        headline: 'Entry Class Code Definitions',
                        content: EntryClassCodeDefinitions,
                      },
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <Dropdown
                          options={['CCD', 'CIE', 'CTX', 'PPD', 'TEL', 'WEB'].map((code: string) => ({
                            label: code,
                            value: code,
                          }))}
                          active={value}
                          fullWidth
                          onChange={onChange}
                          onOpenChange={(open) => !open && onBlur()}
                          hasError={!!error}
                        />
                      ),
                    },
                    {
                      id: 'wireDefaultParameters.description',
                      hide: watch('templateType') !== TransferTemplateType.Wire,
                      label: 'Description',
                      defaultValue: '',
                      children: ({ value, onChange, onBlur }) => (
                        <Input placeholder="Description" value={String(value)} onChange={onChange} onBlur={onBlur} />
                      ),
                    },
                    {
                      id: 'bookDefaultParameters.description',
                      hide: watch('templateType') !== TransferTemplateType.Book,
                      label: 'Description',
                      defaultValue: '',
                      children: ({ value, onChange, onBlur }) => (
                        <Input placeholder="Description" value={String(value)} onChange={onChange} onBlur={onBlur} />
                      ),
                    },
                  ],
                },
                {
                  headline: 'Transfers',
                  description:
                    'Create a list of default transfers. Parameters can be added to or edited when the template is in use.',
                  content: !isLoading ? <EditTemplateTable type={watch('templateType')} /> : null,
                },
              ],
            })}
            <ButtonWrapper>
              <Button type="button" variant="secondary" onClick={handleCancelClick}>
                Cancel
              </Button>
              <ButtonGroup>
                <Button type="submit">
                  {id ? 'Save' : 'Create'}
                  <span className="hide-mobile"> Template</span>
                </Button>
                {id && (
                  <Tooltip
                    content="You do not have permission to send this template."
                    isOpen={!isTemplateSendable ? undefined : false}
                    placement="left"
                  >
                    <Button type="button" onClick={handleReviewClick} isDisabled={!isTemplateSendable}>
                      Review
                    </Button>
                  </Tooltip>
                )}
              </ButtonGroup>
            </ButtonWrapper>
          </Wrapper>
        </Loading>
      </form>
    </FormProvider>
  );
};
